import * as React from 'react';
import { Link } from 'react-router-dom';
import { AppUser } from '../../../../App';
import { Loader } from '../../../../../../components/Loader/Loader';
import { SupportQueue } from '../../../../../../models/supportEmail';
import { getSupportThreads } from '../../../../../../services/superadmin/supportEmail';
import { supportBadge, supportDate, supportError } from '../../../../../../helpers/supportEmail';

interface Props {
  user: AppUser;
}
interface State {
  queue?: SupportQueue;
  skip: number;
  loading: boolean;
  error: string;
}
export class SupportEmails extends React.Component<Props, State> {
  state: State = { skip: 0, loading: true, error: '' };
  private request = 0;
  componentDidMount() {
    this.load(0);
  }
  componentWillUnmount() {
    this.request++;
  }
  load = async (skip = this.state.skip) => {
    const request = ++this.request;
    this.setState({ loading: true, error: '', skip });
    try {
      const queue = await getSupportThreads(this.props.user, skip, 20);
      if (request === this.request) this.setState({ queue, loading: false });
    } catch (error) {
      if (request === this.request) this.setState({ loading: false, error: supportError(error) });
    }
  };
  render() {
    const { loading, error, queue, skip } = this.state;
    return (
      <section className="py-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Support Emails</h2>
          <button className="btn btn-secondary" disabled={loading} onClick={() => this.load()}>
            Refresh
          </button>
        </div>
        {loading && <Loader />}
        {error && (
          <div role="alert" className="alert alert-danger">
            {error}
          </div>
        )}
        {!loading && !error && queue && (
          <>
            {queue.items.length === 0 ? (
              <div className="alert alert-info">No support emails on this page.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      {[
                        'Latest received',
                        'Sender',
                        'Subject',
                        'Classification',
                        'Processing',
                        'Match',
                        'Review',
                        'Manual / risk',
                      ].map(title => (
                        <th key={title}>{title}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {queue.items.map(thread => (
                      <tr key={thread.id}>
                        <td>{supportDate(thread.latestMessageAt)}</td>
                        <td className="text-break">{thread.sender}</td>
                        <td className="text-break">
                          <Link to={'/supportEmails/' + encodeURIComponent(thread.id)}>
                            {thread.subject || '(no subject)'}
                          </Link>
                        </td>
                        <td>{thread.classification || 'Pending'}</td>
                        <td>{thread.processingStatus}</td>
                        <td>
                          <span className={'badge badge-' + supportBadge(thread.matchingStatus)}>
                            {thread.matchingStatus || 'Not matched'}
                          </span>
                        </td>
                        <td>{thread.reviewState || 'Not reviewed'}</td>
                        <td>
                          {thread.requiresManualReview ? 'Investigation required' : '—'}
                          <ul className="pl-3">
                            {thread.riskFlags.map(flag => (
                              <li key={flag}>{flag}</li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <nav aria-label="Support queue pages" className="d-flex align-items-center">
              <button
                className="btn btn-outline-secondary mr-2"
                disabled={skip === 0}
                onClick={() => this.load(Math.max(0, skip - queue.limit))}
              >
                Previous
              </button>
              <span>{queue.items.length ? `${skip + 1}–${skip + queue.items.length}` : '0 results'}</span>
              <button
                className="btn btn-outline-secondary ml-2"
                disabled={!queue.hasMore || skip + queue.limit > 10000}
                onClick={() => this.load(skip + queue.limit)}
              >
                Next
              </button>
            </nav>
          </>
        )}
      </section>
    );
  }
}
